/**
 * Data layer for `chartboost-integration`. Thin typed fetch wrappers over
 * `/api/v1/chartboost-integration`. Raw `fetch()` is confined to this file
 * per the dash contract (D6).
 *
 * Chartboost is a gaming ad network integration partner. Events (install,
 * purchase, level_up) are forwarded server-to-server and deduped on
 * (tenant_id, chartboost_click_id, event_name).
 */

export interface ChartboostIntegrationRow {
	id: number;
	tenant_id: string;
	chartboost_click_id: string;
	event_name: string;
	network_app_id: string;
	ifa: string | null;
	country: string | null;
	payload_json: string;
	forwarded: number;
	created_at: number;
}

export interface ListChartboostIntegrationResponse {
	items: ChartboostIntegrationRow[];
}

export interface ChartboostIntegrationStats {
	tenant_id: string;
	total: number;
	by_event: Record<string, number>;
}

export interface CreateChartboostIntegrationBody {
	chartboost_click_id: string;
	event_name: string;
	network_app_id: string;
	ifa?: string;
	country?: string;
}

export interface CreateChartboostIntegrationResponse {
	ok: boolean;
	chartboost_click_id: string;
	event_name: string;
	network_app_id: string;
	created_at: number;
}

const BASE = "/api/v1/chartboost-integration";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function list(filters?: {
	event_name?: string;
	network_app_id?: string;
}): Promise<ListChartboostIntegrationResponse> {
	const qs = new URLSearchParams();
	if (filters?.event_name) qs.set("event_name", filters.event_name);
	if (filters?.network_app_id) qs.set("network_app_id", filters.network_app_id);
	const url = qs.toString() ? `${BASE}?${qs.toString()}` : BASE;
	const res = await fetch(url, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListChartboostIntegrationResponse>(res);
}

export async function stats(): Promise<ChartboostIntegrationStats> {
	const res = await fetch(`${BASE}/stats`, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ChartboostIntegrationStats>(res);
}

export async function create(
	body: CreateChartboostIntegrationBody,
): Promise<CreateChartboostIntegrationResponse> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body),
	});
	return parseOrThrow<CreateChartboostIntegrationResponse>(res);
}

export const chartboostIntegrationQueryKeys = {
	all: ["chartboost-integration"] as const,
	list: (filters?: { event_name?: string; network_app_id?: string }) =>
		filters && (filters.event_name || filters.network_app_id)
			? ([
					...(["chartboost-integration"] as const),
					"list",
					filters.event_name ?? "",
					filters.network_app_id ?? "",
				] as const)
			: ([...(["chartboost-integration"] as const), "list"] as const),
	stats: () => [...(["chartboost-integration"] as const), "stats"] as const,
	detail: (id: string) =>
		[...(["chartboost-integration"] as const), id] as const,
};
